import React, { useEffect, useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, FileCheck, FileX, Clock, DollarSign, Ban, Building2, Phone, Mail, Copy, Check, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import PortalApplicationsView from '../components/PortalApplicationsView';

const StatCard = ({ icon: Icon, label, value, bgColor, textColor }) => (
  <div className="stat-card bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">{label}</p>
        <p className="text-3xl font-bold text-slate-900 mt-2">{value}</p>
      </div>
      <div className={`p-4 rounded-lg ${bgColor}`}>
        <Icon className={`w-8 h-8 ${textColor}`} />
      </div>
    </div>
  </div>
);

export default function AgencyDashboard() {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [escalationContacts, setEscalationContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedField, setCopiedField] = useState(null);
  const navigate = useNavigate();

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchStats();
    fetchPaymentDetails();
    fetchEscalationContacts();
  }, []);

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast.success(`${fieldName} copied!`);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/stats`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentDetails = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin-bank-details`, {
        headers: getAuthHeaders()
      });
      setPaymentDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch payment details:', error);
    }
  };

  const fetchEscalationContacts = async () => {
    try {
      const response = await axios.get(`${API_URL}/escalation-contacts`, {
        headers: getAuthHeaders()
      });
      setEscalationContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch escalation contacts:', error);
    }
  };

  return (
    <Sidebar>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Agency Dashboard
          </h1>
          <p className="text-slate-600 mt-2">Manage your admission applications</p>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading dashboard...</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <StatCard
                icon={FileText}
                label="Total Applications"
                value={stats?.total_leads || 0}
                bgColor="bg-blue-50"
                textColor="text-blue-600"
              />
              <StatCard
                icon={FileCheck}
                label="Successful"
                value={stats?.successful_leads || 0}
                bgColor="bg-green-50"
                textColor="text-green-600"
              />
              <StatCard
                icon={FileX}
                label="Unsuccessful"
                value={stats?.unsuccessful_leads || 0}
                bgColor="bg-red-50"
                textColor="text-red-600"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <StatCard
                icon={Clock}
                label="Pending Payments"
                value={stats?.pending_payments || 0}
                bgColor="bg-yellow-50"
                textColor="text-yellow-600"
              />
              <StatCard
                icon={DollarSign}
                label="Completed Payments"
                value={stats?.completed_payments || 0}
                bgColor="bg-green-50"
                textColor="text-green-600"
              />
              <StatCard
                icon={Ban}
                label="Ineligible Payments"
                value={stats?.ineligible_payments || 0}
                bgColor="bg-gray-50"
                textColor="text-gray-600"
              />
            </div>

            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Quick Actions</h2>
              <div className="flex gap-4">
                <Button
                  onClick={() => navigate('/apply')}
                  data-testid="quick-action-apply"
                  className="bg-[#0052CC] hover:bg-[#0043A8] text-white"
                >
                  Apply New Admission
                </Button>
                <Button
                  onClick={() => navigate('/applications')}
                  data-testid="quick-action-view-applications"
                  variant="outline"
                  className="border-slate-300"
                >
                  View All Applications
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
              {/* Official Payment Details */}
              <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="payment-details-card">
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="w-5 h-5 text-[#0052CC]" />
                  <h2 className="text-xl font-semibold text-slate-900">Official Payment Details</h2>
                </div>
                {paymentDetails && paymentDetails.account_holder_name ? (
                  <div className="space-y-1">
                    <div className="bg-purple-50 border border-purple-200 rounded p-2 mb-3">
                      <p className="text-xs text-purple-700 text-center">Click any field to copy</p>
                    </div>
                    
                    {/* Bank Account Details */}
                    <div 
                      className="group cursor-pointer hover:bg-slate-50 rounded p-2 transition-colors"
                      onClick={() => copyToClipboard(paymentDetails.account_holder_name, 'Account Holder')}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Account Holder</p>
                          <p className="text-sm text-slate-900 font-medium">{paymentDetails.account_holder_name}</p>
                        </div>
                        {copiedField === 'Account Holder' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100" />}
                      </div>
                    </div>
                    
                    <div 
                      className="group cursor-pointer hover:bg-slate-50 rounded p-2 transition-colors"
                      onClick={() => copyToClipboard(paymentDetails.account_number, 'Account Number')}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Account Number</p>
                          <p className="text-sm text-slate-900 font-medium">{paymentDetails.account_number}</p>
                        </div>
                        {copiedField === 'Account Number' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100" />}
                      </div>
                    </div>
                    
                    <div 
                      className="group cursor-pointer hover:bg-slate-50 rounded p-2 transition-colors"
                      onClick={() => copyToClipboard(paymentDetails.bank_name, 'Bank Name')}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Bank Name</p>
                          <p className="text-sm text-slate-900">{paymentDetails.bank_name}</p>
                        </div>
                        {copiedField === 'Bank Name' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100" />}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div 
                        className="group cursor-pointer hover:bg-slate-50 rounded p-2 transition-colors"
                        onClick={() => copyToClipboard(paymentDetails.ifsc_code, 'IFSC Code')}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">IFSC Code</p>
                            <p className="text-sm text-slate-900">{paymentDetails.ifsc_code}</p>
                          </div>
                          {copiedField === 'IFSC Code' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100" />}
                        </div>
                      </div>
                      <div 
                        className="group cursor-pointer hover:bg-slate-50 rounded p-2 transition-colors"
                        onClick={() => copyToClipboard(paymentDetails.branch, 'Branch')}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Branch</p>
                            <p className="text-sm text-slate-900">{paymentDetails.branch}</p>
                          </div>
                          {copiedField === 'Branch' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100" />}
                        </div>
                      </div>
                    </div>
                    
                    {/* UPI ID Section */}
                    {paymentDetails.upi_id && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <div 
                          className="group cursor-pointer hover:bg-emerald-50 rounded p-2 transition-colors"
                          onClick={() => copyToClipboard(paymentDetails.upi_id, 'UPI ID')}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <CreditCard className="w-4 h-4 text-emerald-600" />
                              <div>
                                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">UPI ID</p>
                                <p className="text-sm text-emerald-700 font-medium">{paymentDetails.upi_id}</p>
                              </div>
                            </div>
                            {copiedField === 'UPI ID' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100" />}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 italic">No payment details available</p>
                )}
              </div>

              {/* Escalation Matrix */}
              <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="escalation-matrix-card">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">Escalation Matrix</h2>
                {escalationContacts.length > 0 ? (
                  <div className="space-y-4">
                    {escalationContacts.map((contact, index) => (
                      <div key={index} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">{contact.level}</p>
                        <p className="text-sm font-medium text-slate-900">{contact.name}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-600">
                          <div className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            <span>{contact.mobile_number}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            <span>{contact.email}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 italic">No escalation contacts available</p>
                )}
              </div>
            </div>

            {/* Portal Applications View with Filters */}
            <div className="mt-8">
              <PortalApplicationsView />
            </div>
          </>
        )}
      </div>
    </Sidebar>
  );
}